<script lang="ts" setup>
const route = useRoute()
const { t } = useI18n()
const { supportRoot, articles: sortedArticles } = await useSupportArticles()

const currentArticle = computed(() => sortedArticles.value.find((article) => article.path === route.path))

const currentTitle = computed(() => currentArticle.value?.title ?? '')
const currentIcon = computed(() => currentArticle.value?.icon ?? 'info')

const crumbs = computed(() => [{ label: t('header.support'), to: supportRoot }, { label: currentTitle.value }])
</script>

<template>
  <div class="support-article">
    <aside class="sidebar">
      <NuxtLink class="all-articles" :to="supportRoot">{{ $t('support.allArticles') }}</NuxtLink>
      <nav>
        <ul>
          <li v-for="article of sortedArticles" :key="article.path">
            <NuxtLink :to="article.path" :class="{ active: article.path === route.path }">
              <SbIcon :name="article.icon ?? 'info'" size="18px" />
              {{ article.title }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </aside>
    <div class="content-column">
      <Breadcrumb :items="crumbs" />
      <SbIcon class="article-icon" :name="currentIcon" size="40px" />
      <article class="prose">
        <slot />
      </article>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.support-article {
  display: flex;
  align-items: flex-start;
  gap: 40px;
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;

  @media screen and (max-width: 800px) {
    // Show the article first, with the article list below it
    flex-direction: column-reverse;
    gap: 24px;
  }
}

.sidebar {
  flex: 0 0 240px;
  position: sticky;
  top: 100px;

  @media screen and (max-width: 800px) {
    position: static;
    flex-basis: auto;
    width: 100%;
  }

  .all-articles {
    display: inline-block;
    margin-bottom: 16px;
    color: var(--text-subtle);
    text-decoration: none;
    font-weight: bold;

    &:hover {
      color: var(--text);
    }
  }

  nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  nav a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    color: var(--text-subtle);
    text-decoration: none;

    &:hover {
      background: var(--background-hover);
      color: var(--text);
    }

    &.active {
      background: var(--background-subtle);
      color: var(--text);
      font-weight: bold;
    }
  }
}

.content-column {
  flex: 1;
  min-width: 0;
  max-width: 720px;
}

.article-icon {
  color: var(--primary);
  display: block;
  margin: 12px 0;
}
</style>
